import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  ReactNode,
} from 'react';

type Tone = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  tone: Tone;
  message: string;
}

interface Ctx {
  success: (msg: string) => void;
  error: (msgOrErr: unknown, fallback?: string) => void;
  info: (msg: string) => void;
}

const ToastContext = createContext<Ctx | undefined>(undefined);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

const PALETTE: Record<Tone, { bg: string; fg: string }> = {
  success: { bg: 'var(--green-100)', fg: 'var(--green-800)' },
  error: { bg: 'var(--red-100)', fg: 'var(--red-900)' },
  info: { bg: 'var(--blue-100)', fg: 'var(--blue-900)' },
};

export const ToastProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const push = useCallback((tone: Tone, message: string) => {
    const id = ++idRef.current;
    setToasts((ts) => [...ts, { id, tone, message }]);
    setTimeout(
      () => setToasts((ts) => ts.filter((t) => t.id !== id)),
      tone === 'error' ? 6000 : 3500,
    );
  }, []);

  const api: Ctx = {
    success: (msg) => push('success', msg),
    info: (msg) => push('info', msg),
    error: (err, fallback = 'Something went wrong.') => {
      // A plain string is a ready-to-show message (e.g. a validation error),
      // not an error object — show it as-is.
      if (typeof err === 'string') {
        push('error', err);
        return;
      }
      const e = err as {
        response?: { data?: { message?: string | string[] } };
        message?: string;
      };
      const msg =
        (Array.isArray(e?.response?.data?.message)
          ? e.response?.data?.message?.[0]
          : e?.response?.data?.message) ??
        e?.message ??
        fallback;
      push('error', String(msg));
    },
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          zIndex: 1000,
          maxWidth: 380,
        }}
      >
        {toasts.map((t) => {
          const p = PALETTE[t.tone];
          return (
            <div
              key={t.id}
              style={{
                background: p.bg,
                color: p.fg,
                padding: '12px 16px',
                borderRadius: 'var(--radius-md)',
                fontSize: 14,
                boxShadow: 'var(--shadow-md)',
              }}
            >
              {t.message}
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};
